/* eslint-disable */
// @ts-nocheck
import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save } from 'lucide-react';
import { useForm, useFieldArray } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { useQueryClient } from 'react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import axiosClient from '@/utils/axiosClient';
import { Label } from '../ui/label';

export interface IContact {
  id?: string;
  name?: string;
  phone?: string;
  metadata?: Record<string, string>;
}

const formSchema = z.object({
  name: z.string().optional(),
  phone: z
    .string()
    .min(1, { message: 'Phone number is required' })
    .refine((val) => /^\+?\d{10,15}$/.test(val.replace(/\D/g, '')), { message: 'Invalid phone number' }),
  metadata: z
    .array(
      z.object({
        key: z.string().min(1, { message: 'Key is required' }),
        value: z.string().min(1, { message: 'Value is required' }),
      }),
    )
    .optional(),
});

function mapContactToFormValues(contact: IContact) {
  return {
    name: contact.name || '',
    phone: contact.phone || '',
    metadata: contact.metadata
      ? Object.keys(contact.metadata).map((key) => ({
          key,
          value: contact.metadata![key],
        }))
      : [],
  };
}

interface ContactFormProps {
  contact?: IContact;
  onClose: () => void;
}

const ContactForm = ({ contact, onClose }: ContactFormProps) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      phone: '',
      metadata: [],
    },
  });

  const { control, handleSubmit } = form;

  const {
    fields: metadataFields,
    append: appendMetadata,
    remove: removeMetadata,
  } = useFieldArray({
    control,
    name: 'metadata',
  });

  useEffect(() => {
    if (contact) {
      form.reset(mapContactToFormValues(contact));
    }
  }, [contact]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    try {
      const metadataObj: Record<string, string> = {};
      if (data.metadata) {
        data.metadata.forEach((m) => {
          metadataObj[m.key] = m.value;
        });
      }

      // Normalize and ensure we have a leading '+'
      const digitsOnly = data.phone.replace(/\D/g, '');
      const finalPhone = data.phone.trim().startsWith('+') ? `+${digitsOnly}` : `+1${digitsOnly}`;

      const payload = {
        name: data.name,
        phone: finalPhone,
        metadata: metadataObj,
      };

      if (contact?.id) {
        await axiosClient.put(`v1/contacts/${contact.id}`, payload);
      } else {
        await axiosClient.post('v1/contacts', payload);
      }

      queryClient.invalidateQueries('v1/contacts');
      onClose();
    } catch (error) {
      console.error('Error saving contact:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-4">
          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Contact</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="John" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <Label htmlFor="phone">Phone Number</Label>
                <FormControl>
                  <InputMask
                    mask="+9 (999) 999-9999"
                    {...field}
                    id="phone"
                    placeholder="Enter your phone number"
                    onChange={(e: any) => field.onChange(e.target.value)}
                  >
                    {(inputProps: any) => <Input type="tel" {...inputProps} />}
                  </InputMask>
                </FormControl>
                {form.formState.errors.phone && <FormMessage>{form.formState.errors.phone.message}</FormMessage>}
              </FormItem>
            )}
          />
        </div>

        {/* Metadata Section */}
        <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <FormLabel>Additional Data Points (Metadata)</FormLabel>
            <Button type="button" onClick={() => appendMetadata({ key: '', value: '' })}>
              Add Row
            </Button>
          </div>
          {metadataFields.map((item, index) => (
            <div key={item.id} className="flex items-center space-x-4">
              <FormField
                control={control}
                name={`metadata.${index}.key`}
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormControl>
                      <Input placeholder="Key" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`metadata.${index}.value`}
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormControl>
                      <Input placeholder="Value" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="button" variant="destructive" onClick={() => removeMetadata(index)}>
                Remove
              </Button>
            </div>
          ))}
        </div>

        <div className="flex justify-end mt-4">
          <Button type="submit" disabled={isSubmitting}>
            <Save className="mr-2" />
            {isSubmitting ? 'Saving Contact...' : 'Save Contact'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default ContactForm;
